/**
 * Tech Ops → Brand BOM Import Reviews tab (Jul 2026).
 *
 * Mirrors `BidsoReviewsTab.jsx`. Shows every PENDING
 * `brand_bom_import_requests` doc; expandable row surfaces the
 * proposed_lines with per-line mode, brand-match status, and
 * artwork checklist. Approve/Reject actions call the review
 * endpoint (BOM:CREATE gated on the backend).
 *
 * Wired from TechOps.js as a new tab next to "Bidso Reviews".
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, RefreshCw, ChevronDown, ChevronRight, AlertTriangle, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_BADGE = {
  LINK: "bg-emerald-100 text-emerald-800 border-emerald-200",
  RECREATE: "bg-amber-100 text-amber-800 border-amber-200",
  SKIP: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const CATEGORY_BADGE = {
  LB: "bg-amber-50 text-amber-700 border-amber-200",
  BS: "bg-violet-50 text-violet-700 border-violet-200",
  PM: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

// Unique testid per line: first occurrence keeps `<source_rm_id>`, later
// duplicates of the same RM get `<source_rm_id>-dup1`, `-dup2`, ...
const lineTestId = (lines, idx) => {
  const rmId = lines[idx]?.source_rm_id;
  const n = lines.slice(0, idx).filter((l) => l.source_rm_id === rmId).length;
  return n === 0 ? rmId : `${rmId}-dup${n}`;
};


/** One review row — collapsed = summary, expanded = per-line table + actions. */
function ReviewRow({ req, onActionDone }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const linkCount = (req.proposed_lines || []).filter((l) => l.mode === "LINK").length;
  const recreateCount = (req.proposed_lines || []).filter((l) => l.mode === "RECREATE").length;
  const skipCount = (req.proposed_lines || []).filter((l) => l.mode === "SKIP").length;
  const mismatchCount = (req.proposed_lines || []).filter((l) => !l.link_allowed).length;
  const missingArtwork = (req.proposed_lines || []).filter(
    (l) => l.mode === "RECREATE" && !(l.artwork_files || []).length
  );

  const act = async (action) => {
    // Backend rejects APPROVE if any RECREATE has empty artwork_files;
    // catch it client-side too for a cleaner UX.
    if (action === "APPROVE" && missingArtwork.length > 0) {
      toast.error(
        `${missingArtwork.length} RECREATE line(s) missing artwork — cannot approve.`
      );
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/demand-hub/brand-bom-import-requests/${req.id}/review`,
        { action, review_notes: notes }
      );
      toast.success(`Request ${action.toLowerCase()}d`);
      onActionDone?.();
    } catch (e) {
      // Oct 2026 — the RM duplicate guard (assert_no_duplicate_rm)
      // returns a STRUCTURED 409 detail ({duplicate, existing_rm_id,
      // existing_description, message}). Surface WHICH existing RM
      // matched — same as the create-RM screen (RMRepository.js:1227)
      // — instead of rendering the dict as "[object Object]".
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail?.duplicate
          ? `Duplicate — an identical RM already exists: ${detail.existing_rm_id}${detail.existing_description ? ` — ${detail.existing_description}` : ""} (status: ${detail.existing_status || "ACTIVE"})`
          : detail?.message || `${action} failed`;
      toast.error(msg, { duration: 7000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid={`brand-bom-review-row-${req.id}`}>
      <CardContent className="p-0">
        {/* Header — collapsed summary */}
        <button
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 text-left"
          onClick={() => setOpen((o) => !o)}
          data-testid={`brand-bom-review-toggle-${req.id}`}
        >
          <div className="flex items-center gap-3">
            {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
            <div>
              <div className="font-mono text-sm">
                {req.target_bidso_sku_id}
                <span className="text-zinc-400 mx-2">·</span>
                <span className="text-xs text-zinc-500">{(req.source_buyer_skus || []).length} source(s)</span>
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                By {req.requester_name || req.requested_by} · {req.requested_at?.slice(0, 16).replace("T", " ")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {mismatchCount > 0 && (
              <Badge className="bg-red-100 text-red-800 border-red-200 text-xs" data-testid={`brand-bom-review-mismatch-${req.id}`}>
                <AlertTriangle className="w-3 h-3 mr-1" />
                {mismatchCount} cross-brand
              </Badge>
            )}
            <Badge className={MODE_BADGE.LINK + " text-xs"}>LINK {linkCount}</Badge>
            <Badge className={MODE_BADGE.RECREATE + " text-xs"}>RECREATE {recreateCount}</Badge>
            {skipCount > 0 && <Badge className={MODE_BADGE.SKIP + " text-xs"}>SKIP {skipCount}</Badge>}
          </div>
        </button>

        {/* Expanded — per-line table + actions */}
        {open && (
          <div className="border-t px-4 py-3 space-y-4">
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-zinc-500">
                  <tr className="border-b">
                    <th className="text-left p-2">Source RM</th>
                    <th className="text-left p-2">Cat</th>
                    <th className="text-left p-2">From</th>
                    <th className="text-left p-2">Brand</th>
                    <th className="text-right p-2">Qty</th>
                    <th className="text-left p-2">Mode</th>
                    <th className="text-left p-2">Artwork</th>
                  </tr>
                </thead>
                <tbody>
                  {(req.proposed_lines || []).map((ln, lnIdx) => (
                    <tr key={`${ln.source_rm_id}-${lnIdx}`} className="border-b" data-testid={`brand-bom-review-line-${lineTestId(req.proposed_lines, lnIdx)}`}>
                      <td className="p-2">
                        <div className="font-mono text-xs">{ln.source_rm_id}</div>
                        <div className="text-[10px] text-zinc-500 truncate max-w-[280px]">
                          {ln.description || ln.rm_name}
                        </div>
                      </td>
                      <td className="p-2">
                        {ln.category && (
                          <Badge className={`${CATEGORY_BADGE[ln.category] || "bg-zinc-100"} text-[10px]`}>
                            {ln.category}
                          </Badge>
                        )}
                      </td>
                      <td className="p-2 font-mono text-[10px] text-zinc-500">
                        {ln.source_buyer_sku?.bidso_sku_id}
                      </td>
                      <td className="p-2">
                        {ln.brand_match ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">match</Badge>
                        ) : (
                          <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]">cross-brand</Badge>
                        )}
                      </td>
                      <td className="p-2 text-right text-xs">
                        {ln.quantity} <span className="text-zinc-400">{ln.unit}</span>
                      </td>
                      <td className="p-2">
                        <Badge className={`${MODE_BADGE[ln.mode] || ""} text-[10px]`}>{ln.mode}</Badge>
                      </td>
                      <td className="p-2 text-xs">
                        {ln.mode === "RECREATE" ? (
                          (ln.artwork_files || []).length > 0 ? (
                            <span className="text-emerald-700 inline-flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              {ln.artwork_files.length} file(s)
                            </span>
                          ) : (
                            <span className="text-red-700 inline-flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              missing
                            </span>
                          )
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Review actions */}
            <div className="space-y-2">
              <Textarea
                placeholder="Review notes (visible to the requester)…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                data-testid={`brand-bom-review-notes-${req.id}`}
              />
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => act("REJECT")}
                  disabled={busy}
                  data-testid={`brand-bom-review-reject-${req.id}`}
                >
                  <X className="w-4 h-4 mr-1" />
                  Reject
                </Button>
                <Button
                  size="sm"
                  onClick={() => act("APPROVE")}
                  disabled={busy || missingArtwork.length > 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`brand-bom-review-approve-${req.id}`}
                  title={missingArtwork.length > 0 ? `${missingArtwork.length} RECREATE line(s) missing artwork` : ""}
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function BrandBomImportReviewsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        // Filter to PENDING on the wire — the review UI only cares
        // about actionable rows. History is available via My Requests.
        const res = await axios.get(
          `${API}/demand-hub/brand-bom-import-requests?status=PENDING`
        );
        if (!cancel) setItems(res.data || []);
      } catch (e) {
        if (!cancel) toast.error(e?.response?.data?.detail || "Failed to load review queue");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [refreshNonce]);

  const triggerRefresh = () => setRefreshNonce((n) => n + 1);

  const visible = items.filter((r) => {
    if (!search.trim()) return true;
    const s = search.trim().toLowerCase();
    return (
      (r.target_bidso_sku_id || "").toLowerCase().includes(s) ||
      (r.requester_name || "").toLowerCase().includes(s) ||
      (r.source_buyer_skus || []).some((sk) => (sk.bidso_sku_id || "").toLowerCase().includes(s))
    );
  });

  return (
    <div className="space-y-4" data-testid="brand-bom-reviews-tab">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-emerald-600" />
            Brand BOM Import Reviews
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Review the request to import a brand-specific BOM into a Buyer SKU.
            RECREATE lines need artwork attached; cross-brand LINK lines are flagged.
          </p>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={triggerRefresh}
          disabled={loading}
          data-testid="brand-bom-reviews-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
          Pending {items.length}
        </Badge>
        <div className="ml-auto w-64">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search target/source/requester…"
            className="h-9"
            data-testid="brand-bom-reviews-search"
          />
        </div>
      </div>

      {loading && items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-zinc-500">Loading…</CardContent></Card>
      ) : visible.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center" data-testid="brand-bom-reviews-empty">
            <Check className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <div className="font-medium">Nothing to review</div>
            <div className="text-sm text-zinc-500 mt-1">
              No brand-BOM import requests are waiting for approval.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="brand-bom-reviews-list">
          {visible.map((req) => (
            <ReviewRow key={req.id} req={req} onActionDone={triggerRefresh} />
          ))}
        </div>
      )}
    </div>
  );
}
